import { Text, View, StyleSheet } from "@react-pdf/renderer";
import { BalancoFinanceiroPdfResponse } from "core/features/relatorios/typings";
import { PDFLayout } from "ui/components/PDF";
import DespesaTable from "./components/DespesaTable";
import MovimentacaoContasBancarias from "./components/MovimentacaoContasBancarias";
import { formatMoney } from "infra/helpers/number";
import ResumoBalancoFinanceiro from "./components/Resumo";
import colors from "core/resources/theme/colors";
import useRelatorioBalancoFinanceiroPdfColumns from "./components/DespesaTable/hooks/useRelatorioBalancoFinanceiroPdfColumns";
import { flattenRelatorioCategoriasTree } from "core/features/relatorios/utils/flattenRelatorioCategoriasTree";
import ReceitaTable from "./components/ReceitaTable";
import PDFTableTitleRow from "components/PDF/Table/TableTitleRow";

interface PDFRelatorioBalancoFinanceiroProps {
  data?: BalancoFinanceiroPdfResponse;
  includeDocumentWrapper?: boolean;
}

function PDFRelatorioBalancoFinanceiro({
  data,
  includeDocumentWrapper = true,
}: PDFRelatorioBalancoFinanceiroProps) {
  const flattenReceitas = flattenRelatorioCategoriasTree(
    data?.receita?.categorias || []
  );
  const columns = useRelatorioBalancoFinanceiroPdfColumns();
  return (
    <PDFLayout
      includeDocument={includeDocumentWrapper}
      fontSize={7}
      lineHeight={0.95}
      assinaturas={data?.assinaturas}
      headerProps={{
        logo: data?.logo!,
        tenant: data?.nomeTenant!,
        title: `Relatório de Balanço Financeiro`,
        subTitle: data?.periodo,
        cards: [
          {
            title: "Total de Receitas",
            value: formatMoney(data?.totalReceita),
          },
          {
            title: "Total de Despesas",
            value: formatMoney(data?.totalDespesa),
          },
        ],
      }}
      footerProps={{
        tenant: data?.nomeTenant!,
        logo: data?.logoAdm!,
        endereco1: data?.endereco1!,
        endereco2: data?.endereco2!,
      }}
    >
      {data?.receita?.categorias?.length ? (
        <Text
          style={{
            textAlign: "center",
            fontSize: 10,
            fontWeight: "bold",
            marginBottom: 6,
          }}
        >
          HISTÓRICO DE RECEITAS
        </Text>
      ) : null}

      <ReceitaTable items={flattenReceitas} tableTitle="" />

      {data?.receita?.categorias?.length ? (
        <View style={{ ...styles.styleLinhaTotal, marginTop: 6 }}>
          <Text
            style={{
              fontWeight: "bold",
              marginRight: 16,
              textAlign: "right",
            }}
          >
            TOTAL DE RECEITAS
          </Text>

          <Text
            style={{
              fontWeight: "bold",
              width: 100,
              textAlign: "right",
            }}
          >
            {formatMoney(data?.totalReceita)}
          </Text>
        </View>
      ) : null}

      {data?.despesa?.categorias?.length ? (
        <Text
          style={{
            textAlign: "center",
            fontSize: 10,
            fontWeight: "bold",
            marginTop: 4,
            marginBottom: 6,
          }}
        >
          HISTÓRICO DE DESPESAS
        </Text>
      ) : null}
      <View>
        <PDFTableTitleRow columns={columns} variant="rowLike" />
      </View>
      {data?.despesa?.categorias.map((item) => (
        <>
          {item.categoriaFinanceiraPai ? (
            <View>
              <Text style={{ fontWeight: "bold", marginBottom: 2 }}>
                {item.categoriaFinanceiraPai.breadcrumb}{" "}
                {item.categoriaFinanceiraPai.nome}
              </Text>
            </View>
          ) : null}
          <View
            key={item.id}
            style={{
              marginBottom: 4,
            }}
          >
            <DespesaTable item={item} />
          </View>
        </>
      ))}
      {data?.despesa?.categorias?.length ? (
        <View style={styles.styleLinhaTotal}>
          <Text
            style={{
              fontWeight: "bold",
              textAlign: "right",
              marginRight: 16,
            }}
          >
            TOTAL DE DESPESAS
          </Text>

          <Text
            style={{
              fontWeight: "bold",
              width: 100,
              textAlign: "right",
            }}
          >
            {formatMoney(data?.totalDespesa)}
          </Text>
        </View>
      ) : null}

      {data ? <ResumoBalancoFinanceiro data={data} /> : null}

      {data?.contasBancarias?.length ? (
        <MovimentacaoContasBancarias data={data} />
      ) : null}
    </PDFLayout>
  );
}

const styles = StyleSheet.create({
  styleLinhaTotal: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginVertical: 2,
    paddingRight: 4,
    paddingTop: 1,
    paddingBottom: 3,
    fontSize: 7.5,
    lineHeight: 1,
    backgroundColor: colors.black100,
    alignItems: "center",
  },
});

export default PDFRelatorioBalancoFinanceiro;
